import React, { ReactNode } from "react";
import { Grid, CircularProgress } from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import CardsDisplay from "../../components/Shared/Cards/CardsDisplay";
import {
  useGetDBStorageQuery,
  useGetKPIStorageQuery,
  useGetPMFileStorageQuery,
  useGetPVCStorageQuery,
} from "../../store/api/admin/adminApiSlice";
import { API } from "../../globals/globals";
import { byteToGibiByte } from "../../utils/helpers";
import { useGetNodesCountsQuery } from "../../store/api/devices/deviceApiSlice";
import { useSelector } from "react-redux";
import { selectedGeoRegionIdsSelector } from "../../store/filters/Nodes/selectors";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface DeviceCardConfig {
  title: string;
  icon: ReactNode;
  getValue: (data: any) => number;
  getTotal?: (data: any) => number;
  getPercentage: (data: any) => number;
  percentageLabel: string;
  /** "up" → green (success), "down" → red (destructive) */
  //trend: "up" | "down";
}

interface StorageCardConfig {
  title: string;
  queryResult: any;
}

/* ------------------------------------------------------------------ */
/*  Donut segment colors (matches AMS token palette)                 */
/* ------------------------------------------------------------------ */

const SEGMENT_COLORS = {
  free: "var(--primary)",
  used: "var(--success)",
  total: "var(--warning)",
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const Capacity: React.FC = () => {
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);
  const deviceCountData = useGetNodesCountsQuery(
    { geoRegionIds: selectedGeoRegionIds },
    {
      pollingInterval: API.ENDPOINTS.NODESCOUNTS.pollingInterval || API.POLLING_INTERVAL,
    }
  );
  const kpiStorage = useGetKPIStorageQuery(null, {
    pollingInterval: API.ENDPOINTS.KPISTORAGE.pollingInterval || API.POLLING_INTERVAL,
  });

  const pvcStorage = useGetPVCStorageQuery(null, {
    pollingInterval: API.ENDPOINTS.PVCSTORAGE.pollingInterval || API.POLLING_INTERVAL,
  });

  const pmFileStorage = useGetPMFileStorageQuery(null, {
    pollingInterval: API.ENDPOINTS.PMFILESTORAGE.pollingInterval || API.POLLING_INTERVAL,
  });

  const dbStorage = useGetDBStorageQuery(null, {
    pollingInterval: API.ENDPOINTS.DBSTORAGE.pollingInterval || API.POLLING_INTERVAL,
  });

  /* ---------------------------------------------------------------- */
  /*  Device summary card renderer                                     */
  /* ---------------------------------------------------------------- */

  const renderDeviceCard = (config: DeviceCardConfig, data: any) => {
    const value = config.getValue(data);
    const total = config.getTotal?.(data);
    const pct = config.getPercentage(data);
    //const trendColor = config.trend === "up" ? "var(--success)" : "var(--destructive)";

    return (
      <div>
        {/* Value row — uniform size per AMS StatCard */}
        <div className="text-2xl font-semibold tracking-tight" style={{ color: "var(--foreground)" }}>
          {value}
          {total !== undefined && <span> / {total}</span>}
        </div>

        {/* Percentage + label */}
        <div className="mt-1 flex items-baseline gap-1 text-sm">
          <span className="inline-flex items-baseline gap-0.5 font-medium" style={{ color: "var(--muted-foreground)" }} >
            {/* <TrendArrow direction={config.trend} color={trendColor} /> */}
            {pct}%
          </span>
          <span style={{ color: "var(--muted-foreground)" }}>{config.percentageLabel}</span>
        </div>
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Storage donut card renderer                                      */
  /* ---------------------------------------------------------------- */

  const renderStorageCard = (config: StorageCardConfig) => {
    const { queryResult } = config;
    const isLoading = queryResult?.isLoading;
    const data = queryResult?.data;

    if (isLoading || !data) {
      return (
        <div className="flex items-center justify-center py-6">
          <CircularProgress size={32} color="primary" />
        </div>
      );
    }

    const usedBytes = data?.used || 0;
    const totalBytes = data?.total || 0;
    const usagePercentage = totalBytes > 0 ? ((usedBytes / totalBytes) * 100).toFixed(2) : "0";
    const freeGB = byteToGibiByte(data.available);
    const usedGB = byteToGibiByte(data.used);
    const totalGB = byteToGibiByte(data.total);

    return (
      <div className="flex items-center gap-6">
        {/* Pure SVG donut — matches AMS KpiDonutCard, no clipping */}
        <SvgDonut
          segments={[
            { name: "Free", value: freeGB, color: SEGMENT_COLORS.free },
            { name: "Used", value: usedGB, color: SEGMENT_COLORS.used },
          ]}
        />

        {/* Right side — title + KPI value + legend (title here, not in card header) */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold" style={{ color: "var(--muted-foreground)" }}>
            {config.title}
          </p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-semibold tracking-tight" style={{ color: "var(--foreground)" }}>
              {usedGB} GB
            </span>
            <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>
              {usagePercentage}% used
            </span>
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-col gap-1.5">
            <LegendItem color={SEGMENT_COLORS.free} label={`${freeGB} GB Free`} />
            <LegendItem color={SEGMENT_COLORS.used} label={`${usedGB} GB Used`} />
            <LegendItem color={SEGMENT_COLORS.total} label={`${totalGB} GB Total`} />
          </div>
        </div>
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Card data configs                                                */
  /* ---------------------------------------------------------------- */

  const deviceCards: DeviceCardConfig[] = [
    {
      title: "Online Devices",
      icon: <Inventory2Icon sx={{ fontSize: 16, color: "var(--muted-foreground)" }} />,
      getValue: (data) => (data?.allDeviceCount || 0) - (data?.offLineDeviceCount || 0),
      getTotal: () => 500,
      getPercentage: (data) => {
        const total = data?.allDeviceCount || 0;
        return total ? Number((((total - (data?.offLineDeviceCount || 0)) / 500) * 100).toFixed(2)) : 0;
      },
      percentageLabel: "of capacity",
      //trend: "up",
    },
    {
      title: "Offline Devices",
      icon: <PowerOffIcon sx={{ fontSize: 16, color: "var(--muted-foreground)" }} />,
      getValue: (data) => data?.offLineDeviceCount ?? 0,
      getTotal: () => 3000,
      getPercentage: (data) => {
        const offline = data?.offLineDeviceCount ?? 0;
        return Number(((offline / 3000) * 100).toFixed(2));
      },
      percentageLabel: "of total devices",
      //trend: "down",
    },
    {
      title: "Ungrouped Devices",
      icon: <Inventory2Icon sx={{ fontSize: 16, color: "var(--muted-foreground)" }} />,
      getValue: (data) => data?.ungroupedCount || 0,
      getTotal: (data) => data?.allDeviceCount || 0,
      getPercentage: (data) => {
        const total = data?.allDeviceCount || 0;
        const ungrouped = data?.ungroupedCount || 0;
        return total ? Number(((ungrouped / total) * 100).toFixed(2)) : 0;
      },
      percentageLabel: "of total devices",
      //trend: "down",
    },
  ];

  const storageCards: StorageCardConfig[] = [
    { title: "Log Files Storage", queryResult: pvcStorage },
    { title: "PM Files Storage", queryResult: pmFileStorage },
    { title: "AMS Database", queryResult: dbStorage },
    { title: "KPI Database", queryResult: kpiStorage },
  ];

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <div
      className="p-4 md:px-8 pt-2 space-y-8"
      style={{ backgroundColor: "var(--background)", minHeight: "100%" }}
    >
      {/* Device Summary Cards — 3 columns on md+ */}
      <Grid container spacing={3}>
        {deviceCards.map((card) => (
          <Grid key={card.title} item xs={12} sm={6} md={4}>
            <CardsDisplay
              title={card.title}
              titleRight={card.icon}
              sx={{ backgroundColor: "var(--card)", height: "100%" }}
              contents={
                deviceCountData?.isLoading ? (
                  <div className="flex justify-center items-center" style={{ minHeight: 50 }}><CircularProgress size={24} /></div>
                ) : (
                  renderDeviceCard(card, deviceCountData?.data?.totals)
                )
              }
            />
          </Grid>
        ))}
      </Grid>

      {/* Storage Donut Cards — 2 columns on md+ */}
      <Grid container spacing={4} mt={"15px !important"}>
        {storageCards.map((card) => (
          <Grid key={card.title} item xs={12} md={6}>
            <CardsDisplay
              sx={{ backgroundColor: "var(--card)", height: "100%" }}
              contents={renderStorageCard(card)}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Trend arrow (matches AMS StatCard TrendArrow)                     */
/* ------------------------------------------------------------------ */

const TrendArrow: React.FC<{ direction: "up" | "down"; color: string }> = ({ direction, color }) => {
  return direction === "up" ? (
    <svg
      className="shrink-0 self-center"
      style={{ width: 16, height: 16, color }}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 17a.75.75 0 0 1-.75-.75V5.612L5.29 9.77a.75.75 0 0 1-1.08-1.04l5.25-5.5a.75.75 0 0 1 1.08 0l5.25 5.5a.75.75 0 1 1-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0 1 10 17Z"
        clipRule="evenodd"
      />
    </svg>
  ) : (
    <svg
      className="shrink-0 self-center"
      style={{ width: 16, height: 16, color }}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 3a.75.75 0 0 1 .75.75v10.638l3.96-4.158a.75.75 0 1 1 1.08 1.04l-5.25 5.5a.75.75 0 0 1-1.08 0l-5.25-5.5a.75.75 0 1 1 1.08-1.04l3.96 4.158V3.75A.75.75 0 0 1 10 3Z"
        clipRule="evenodd"
      />
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/*  Small legend dot component                                         */
/* ------------------------------------------------------------------ */

const LegendItem: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className="flex items-center gap-2 text-sm">
    <span
      className="inline-block h-2 w-2 rounded-full flex-shrink-0"
      style={{ backgroundColor: color }}
    />
    <span style={{ color: "var(--muted-foreground)" }}>{label}</span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Pure SVG donut (matches AMS KpiDonutCard — no clipping issues)    */
/* ------------------------------------------------------------------ */

interface DonutSegment {
  name: string;
  value: number;
  color: string;
}

const DONUT_SIZE = 120;
const DONUT_STROKE = 14;
const DONUT_RADIUS = (DONUT_SIZE - DONUT_STROKE) / 2;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

const SvgDonut: React.FC<{ segments: DonutSegment[] }> = ({ segments }) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0);

  let cumulativeOffset = 0;
  const arcs = segments.map((seg) => {
    const fraction = total > 0 ? seg.value / total : 0;
    const dashLength = fraction * DONUT_CIRCUMFERENCE;
    const gap = DONUT_CIRCUMFERENCE - dashLength;
    const offset = -cumulativeOffset + DONUT_CIRCUMFERENCE * 0.25; // start from top
    cumulativeOffset += dashLength;
    return { ...seg, dasharray: `${dashLength} ${gap}`, dashoffset: offset };
  });

  return (
    <div className="shrink-0" style={{ width: DONUT_SIZE, height: DONUT_SIZE }}>
      <svg width={DONUT_SIZE} height={DONUT_SIZE} viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}>
        {/* Background track */}
        <circle
          cx={DONUT_SIZE / 2}
          cy={DONUT_SIZE / 2}
          r={DONUT_RADIUS}
          fill="none"
          stroke="var(--muted)"
          strokeWidth={DONUT_STROKE}
          opacity={0.3}
        />
        {/* Segments */}
        {arcs.map((arc) => (
          <circle
            key={arc.name}
            cx={DONUT_SIZE / 2}
            cy={DONUT_SIZE / 2}
            r={DONUT_RADIUS}
            fill="none"
            stroke={arc.color}
            strokeWidth={DONUT_STROKE}
            strokeDasharray={arc.dasharray}
            strokeDashoffset={arc.dashoffset}
            strokeLinecap="round"
          />
        ))}
      </svg>
    </div>
  );
};

export default Capacity;